import { useCallback, useEffect, useState } from 'react';
import { departmentsAPI } from '../services/api';
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const emptyDepartmentForm = {
  name: '',
  type: 'academic',
};

const Departments = () => {
  const { hasPermission } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyDepartmentForm);

  const loadDepartments = useCallback(async () => {
    const res = await departmentsAPI.getAll();
    setDepartments(res.data.departments);
  }, []);

  useEffect(() => {
    loadDepartments().catch(() => toast.error('Failed to load departments'));
  }, [loadDepartments]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      if (editingId) {
        await departmentsAPI.update(editingId, form);
        toast.success('Department updated successfully');
      } else {
        await departmentsAPI.create(form);
        toast.success('Department created successfully');
      }
      setForm(emptyDepartmentForm);
      setEditingId(null);
      setShowForm(false);
      await loadDepartments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (department) => {
    setEditingId(department.id);
    setForm({
      name: department.name || '',
      type: department.type || 'academic',
    });
    setShowForm(true);
  };

  const handleDelete = async (departmentId) => {
    if (!window.confirm('Delete this department? Linked records will be detached where supported.')) {
      return;
    }

    try {
      await departmentsAPI.remove(departmentId);
      toast.success('Department deleted successfully');
      await loadDepartments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete department');
    }
  };

  const canManage = hasPermission('departments.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Departments</h1>
          <p className="text-gray-500 text-sm mt-1">Create and edit academic or corporate departments.</p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setEditingId(null);
              setForm(emptyDepartmentForm);
              setShowForm((prev) => !prev);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : 'Add Department'}
          </button>
        )}
      </div>

      {showForm && canManage && (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Department Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Computer Science"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="academic">Academic</option>
              <option value="corporate">Corporate</option>
            </select>
          </div>

          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving...' : editingId ? 'Update Department' : 'Create Department'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyDepartmentForm); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {departments.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No departments found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Department</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {departments.map((department) => (
                <tr key={department.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-blue-600" />
                      {department.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize text-gray-600">{department.type}</td>
                  <td className="px-4 py-3 text-gray-600">{new Date(department.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {canManage ? (
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(department)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50">
                          <Pencil size={12} />
                          Edit
                        </button>
                        <button onClick={() => handleDelete(department.id)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Departments;
